import React, { useEffect, useState } from 'react';
// Même image vide que les box image de l'éditeur.
import placeholderImage from '../../../images/placeholder-img.png';
import styles from './TableConfigBody.module.css';
import { buildTablePreviewRows } from '../tablePreview';
import {
  canMergeRowRange, isMultiSelectClick, mergeLabelRowRange, mergeRuleKey, unmergeLabelRow,
} from '../tableStructure';
import { convertPadding, getCellStyle, getHeaderStyle } from '../tableCellStyle';
import { cellStyleEntry, findRowStyle, rowStyleCss } from '../tableRowStyle';

/**
 * Corps de l'écran « Configurer le tableau », partagé entre le Proposal
 * classique et l'éditeur web.
 *
 * Ce fichier porte **la disposition et rien d'autre** : quels blocs, dans quel
 * ordre, avec quels espacements. Les contrôles eux-mêmes arrivent par `widgets`
 * — Ant Design côté Proposal, Mantine côté web — pour que l'éditeur web n'ait
 * pas à embarquer antd pour un seul panneau.
 *
 * Exception assumée à la règle de `src/shared/` (modules purs) : ce sous-dossier
 * `ui/` a le droit d'être du React. Ce qu'il n'a toujours pas le droit d'avoir :
 * une bibliothèque de composants, le store, Puck.
 *
 * Contrat `widgets` — chacun rend un contrôle, sans libellé (le libellé est
 * posé ici) :
 *   Radio({ value, options, onChange, ariaLabel })   options: [{ value, label, disabled }]
 *   Select({ value, options, onChange, ariaLabel })
 *   Switch({ checked, disabled, onChange, ariaLabel })
 *   Checkbox({ checked, onChange, ariaLabel })
 *   TextArea({ value, onChange, ariaLabel })
 *   NumberInput({ value, min, max, disabled, onChange, title, ariaLabel })
 *   IconButton({ name, title, disabled, onClick, ariaLabel })   name: reset|up|down
 *   TextButton({ name, children, disabled, onClick, title })
 *   Grip()
 *   Spinner()
 *
 * Contrat `dragAdapter` : `useColumnDrag(label)` renvoie
 * `{ rowProps, handleProps, dragging, dropTarget }`. Le classique le branche sur
 * react-dnd, le web sur le drag HTML natif — la ligne, elle, est la même.
 *
 * `previewStyle` : `{ tableStyle, configuration }`, le style du tableau du
 * document et la configuration qui en donne les polices par défaut. L'aperçu
 * se rend alors comme le document (cf. tableCellStyle), style des lignes
 * (`config.rowStyles`) compris ; sans lui, un aperçu neutre.
 *
 * `onSelectedRowChange(row, column)` : ligne cliquée dans l'aperçu, `{ label, kind }`
 * ou null, et libellé d'origine de la colonne de la case cliquée. Le Proposal en règle le style dans son panel de droite.
 *
 * `preview` : aperçu fourni par l'hôte à la place de celui-ci (tableau custom,
 * cf. CustomTablePreview). `onToggleHidden`, `onResetName`, `onResetAllNames`
 * absents : pas de case « visible » ni de retour au nom d'origine (un tableau
 * custom n'a ni colonne masquée ni nom d'origine). `column.name` : nom lu par les
 * lecteurs d'écran quand `label` n'est qu'une clé.
 */

// Alignement d'une colonne du catalogue, rendu comme au document (convertAlign
// du tableau).
const TEXT_ALIGN = { center: 'center', end: 'right', justify: 'justify' };
const toTextAlign = (align) => TEXT_ALIGN[align] ?? 'left';

const NO_DRAG = { rowProps: null, handleProps: null, dragging: false, dropTarget: false };
const useNoDrag = () => NO_DRAG;

function ColumnRow({
  column, index, total, isLastVisible, widgets, useColumnDrag, t,
  onToggleHidden, onRename, onResetName, onWidth, onAlign, onMove,
}) {
  const {
    rowProps, handleProps, dragging, dropTarget,
  } = useColumnDrag(column.label);
  const renamed = column.customLabel != null && column.customLabel !== column.label;
  // Nom lu par les lecteurs d'écran : `name` quand `label` n'est qu'un
  // identifiant (clé de colonne d'un tableau custom).
  const name = column.name ?? column.label;

  return (
    <div
      {...rowProps}
      className={styles.columnRow}
      data-hidden={column.hidden || undefined}
      data-dragging={dragging || undefined}
      data-drop-target={dropTarget || undefined}
    >
      <span {...handleProps} className={styles.grip} aria-label={`${t('table-column-move')} ${name}`}>
        <widgets.Grip />
      </span>
      {onToggleHidden && (
        <widgets.Checkbox
          checked={!column.hidden}
          onChange={() => onToggleHidden(column.label)}
          ariaLabel={`${t('table-column-visible')} ${name}`}
        />
      )}
      <div className={styles.columnLabel}>
        <widgets.TextArea
          value={column.customLabel ?? column.label}
          onChange={(value) => onRename(column.label, value)}
          ariaLabel={`${t('table-column-label')} ${name}`}
        />
        {renamed && onResetName && (
          <widgets.IconButton
            name="reset"
            title={t('bpu-reset-column-name')}
            ariaLabel={`${t('bpu-reset-column-name')} ${name}`}
            onClick={() => onResetName(column.label)}
          />
        )}
      </div>
      <div className={styles.width}>
        <widgets.NumberInput
          value={column.width}
          min={1}
          max={100}
          disabled={isLastVisible}
          title={isLastVisible ? t('bpu-width-last-column-auto') : ''}
          ariaLabel={`${t('table-column-width')} ${name}`}
          onChange={(value) => onWidth(column.label, value)}
        />
      </div>
      {onAlign && (
        <widgets.IconButton
          name={`align-${column.align === 'center' || column.align === 'end' ? column.align : 'start'}`}
          title={t('table-column-align')}
          ariaLabel={`${t('table-column-align')} ${name}`}
          onClick={() => onAlign(column.label)}
        />
      )}
      <widgets.IconButton
        name="up"
        title={t('move-up')}
        disabled={index === 0}
        ariaLabel={`${t('move-up')} ${name}`}
        onClick={() => onMove(index, -1)}
      />
      <widgets.IconButton
        name="down"
        title={t('move-down')}
        disabled={index === total - 1}
        ariaLabel={`${t('move-down')} ${name}`}
        onClick={() => onMove(index, 1)}
      />
    </div>
  );
}

export default function TableConfigBody({
  t,
  controls,
  config,
  capabilities,
  columns,
  columnsConfigurable,
  loading,
  widgets,
  dragAdapter,
  renderControl,
  columnsUnavailableNote = null,
  banner = null,
  previewNotice = null,
  previewRows = 3,
  previewLayout = null,
  previewSample = null,
  previewStyle = null,
  preview = null,
  mergeOptions = null,
  onMergeChange = null,
  onSelectedRowChange = null,
  showMergeSwitches = true,
  onAxisChange,
  onToggleHidden = null,
  onRename,
  onResetName = null,
  onResetAllNames = null,
  onWidth,
  onAlign = null,
  onMove,
}) {
  const useColumnDrag = dragAdapter?.useColumnDrag || useNoDrag;
  const visible = columns.filter((c) => !c.hidden);
  const lastVisibleLabel = visible.length > 1 ? visible[visible.length - 1].label : null;
  const hasAnyCustomLabel = columns.some(
    (c) => c.customLabel != null && c.customLabel !== c.label,
  );
  // Le backend écrit les montants TOTAL HT / TVA / TTC dans une seule colonne :
  // masquée, ces lignes n'ont plus de montant, sans autre signe dans le document.
  const totalsColumn = previewLayout?.totalsColumn;
  const totalsColumnHidden = !!totalsColumn &&
    columns.some((c) => c.label === totalsColumn && c.hidden);
  // Lignes d'exemple de la variante (libellés et totaux à leur place) ; sans
  // forme déclarée, l'aperçu générique « Ligne N ».
  const shapedRows = previewLayout
    ? buildTablePreviewRows(previewLayout.shape, visible.map((c) => c.label), previewLayout, mergeOptions)
    : null;
  // « Ligne N » va dans la première colonne de texte : une colonne image (logo)
  // montre une image, comme dans le document.
  const sampleLabelColumn = visible.find((c) => c.type !== 'image')?.label;
  const mergeOverrides = mergeOptions?.overrides ?? [];
  const setMergeOption = (field, value) => onMergeChange({ ...(mergeOptions ?? {}), [field]: value });
  // Fusion choisie dans l'aperçu, comme dans un tableau custom : un clic
  // sélectionne une cellule d'une ligne-titre ou de total, Maj + clic étend la
  // sélection dans la même ligne, puis « Fusionner ». La règle
  // vaut pour toutes les lignes de ce libellé (ou de ce type : les zones).
  // Seul moyen en modèle, où le tableau du document reste vide.
  const [selection, setSelection] = useState(null); // { rowKey, anchor: [lo, hi], c0, c1 }
  const canSelectRow = (row) => !!onMergeChange && !!row.labelMerge;
  // Cellules couvertes par la case cliquée : toute la fusion pour une ancre.
  const cellSpan = (row, colIdx) => (
    row.merge && colIdx === row.merge.from
      ? [row.merge.from, row.merge.from + row.merge.span - 1]
      : [colIdx, colIdx]
  );
  const selectCell = (row, colIdx, event) => {
    if (!canSelectRow(row)) return;
    const [lo, hi] = cellSpan(row, colIdx);
    const extend = isMultiSelectClick(event);
    if (extend && selection?.rowKey === row.key) {
      const [alo, ahi] = selection.anchor;
      setSelection({ ...selection, c0: Math.min(alo, lo), c1: Math.max(ahi, hi) });
    } else {
      setSelection({ rowKey: row.key, anchor: [lo, hi], c0: lo, c1: hi });
    }
  };
  const selectedRow = selection && shapedRows?.find((row) => row.key === selection.rowKey);
  const isCellSelected = (row, colIdx) => {
    if (!selectedRow || row.key !== selectedRow.key) return false;
    const [lo, hi] = cellSpan(row, colIdx);
    return hi >= selection.c0 && lo <= selection.c1;
  };
  // Annoncée par son identité seule (type ou libellé) : la règle de style vaut
  // pour toutes les lignes de ce type ou de ce libellé.
  const selectedLabelMerge = selectedRow?.labelMerge;
  const selectedIdentity = selectedLabelMerge
    ? { label: selectedLabelMerge.label, ...(selectedLabelMerge.kind && { kind: selectedLabelMerge.kind }) }
    : null;
  const selectedIdentityKey = selectedIdentity && mergeRuleKey(selectedIdentity);
  // Colonne de la case cliquée (celle de son contenu pour une fusion) : les
  // réglages « cette colonne seulement » du panel s'y rangent.
  const anchorIdx = selection?.anchor[0];
  const selectedColumn = selectedRow && anchorIdx != null
    ? ((selectedRow.merge && anchorIdx === selectedRow.merge.from && selectedRow.merge.contentColumn) || visible[anchorIdx]?.label)
    : null;
  useEffect(() => {
    onSelectedRowChange?.(selectedIdentity, selectedColumn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdentityKey, selectedColumn]);
  // Une ligne au style seul (cases remplies, cf. getRowStyleState) ne fusionne pas.
  const selectionMergeable = !!selectedRow && !selectedLabelMerge.styleOnly && canMergeRowRange(
    selection.c0,
    selection.c1,
    (idx) => selectedRow.cells[visible[idx].label] !== ''
  );
  const mergeSelection = () => {
    setMergeOption('overrides', mergeLabelRowRange(mergeOverrides, selectedRow.labelMerge, {
      from: visible[selection.c0].label,
      to: visible[selection.c1].label,
    }));
    setSelection(null);
  };
  const unmergeSelection = () => {
    setMergeOption('overrides', unmergeLabelRow(mergeOverrides, selectedRow.labelMerge));
    setSelection(null);
  };
  const removeRule = (rule) => setMergeOption(
    'overrides',
    mergeOverrides.filter((o) => mergeRuleKey(o) !== mergeRuleKey(rule))
  );
  // Une règle par type (les zones) n'a pas de libellé : elle vaut pour toutes.
  const ruleName = (rule) => (rule.kind ? t(`table-merge-kind-${rule.kind}`) : `« ${rule.label} »`);
  const columnName = (label) => {
    const column = visible.find((col) => col.label === label);
    return column?.customLabel ?? label;
  };
  const ruleRange = (rule) => {
    if (!rule.merge) return t('table-merge-override-off');
    if (!rule.from && !rule.to) return t('table-merge-override-on');
    return `${columnName(rule.from) ?? '…'} → ${columnName(rule.to) ?? '…'}`;
  };

  // Style du document : cases rendues par les fonctions de Cell / Header,
  // l'espacement sur un bloc intérieur comme chez eux, puis le style de leur
  // ligne. `lastIdx` : dernière colonne couverte (une fusion qui va au bout
  // porte la bordure droite).
  const rowRules = shapedRows?.map((row) => findRowStyle(config.rowStyles, row.labelMerge)) ?? [];
  const spacing = previewStyle && convertPadding(
    previewStyle.tableStyle.size,
    previewStyle.tableStyle.customCellPadding,
    previewStyle.tableStyle.customCellPaddingUnit,
  );
  const columnAlign = (label) => visible.find((col) => col.label === label)?.align;
  const headerStyle = (label, lastIdx) => previewStyle && {
    ...getHeaderStyle({ ...previewStyle, isLastColumn: lastIdx === visible.length - 1 }),
    textAlign: toTextAlign(columnAlign(label)),
    ...rowStyleCss(null, rowRules[0]),
  };
  const cellStyle = (rowIdx, label, lastIdx, firstIdx = 0) => {
    if (!previewStyle) return previewStyle;
    // Réglages propres à cette colonne de la ligne, par-dessus ceux de la ligne.
    const cellEntry = cellStyleEntry(rowRules[rowIdx], label);
    return {
      ...getCellStyle({
        ...previewStyle,
        isEven: rowIdx % 2 === 0,
        isLastColumn: lastIdx === visible.length - 1,
        width: 'auto',
      }),
      textAlign: toTextAlign(cellEntry?.align ?? rowRules[rowIdx]?.align ?? columnAlign(label)),
      ...rowStyleCss(rowRules[rowIdx], rowRules[rowIdx + 1], {
        isFirstColumn: firstIdx === 0,
        isLastColumn: lastIdx === visible.length - 1,
      }),
      ...rowStyleCss(cellEntry),
    };
  };
  // Une case vide du document garde une ligne de texte (éditeur vide).
  const cellContent = (text) => (previewStyle ? (
    <div className={styles.previewCellContent} style={{ padding: spacing.padding, minHeight: spacing.minHeight }}>
      {text || ' '}
    </div>
  ) : text);

  // Une option peut dépendre d'une capability du backend (ex. le BPU en travaux
  // supplémentaires). Elle reste **affichée** et seulement désactivée : une
  // option qui disparaît laisse croire que la variante n'existe pas. Et une
  // capability inconnue vaut « disponible » — le backend ne renvoie ses
  // capabilities que pour certains métiers.
  const axisOptions = (control) => (control.options || []).map((o) => ({
    value: o.value,
    label: t(o.labelKey),
    disabled: !!o.capability && !(capabilities?.[o.capability] ?? true),
  }));

  const defaultControl = (control) => {
    const label = t(control.labelKey);
    if (control.type === 'switch') {
      return (
        <div key={control.field} className={styles.switchRow}>
          <span className={styles.fieldLabel} style={{ marginBottom: 0 }}>{label}</span>
          <widgets.Switch
            ariaLabel={label}
            checked={!!config[control.field]}
            disabled={!!control.enabledWhenVariant && config.variant !== control.enabledWhenVariant}
            onChange={(checked) => onAxisChange(control.field, checked)}
          />
        </div>
      );
    }
    const Control = control.type === 'radio' ? widgets.Radio : widgets.Select;
    return (
      <div key={control.field} className={styles.field}>
        <div className={styles.fieldLabel}>{label}</div>
        <Control
          ariaLabel={label}
          value={config[control.field]}
          options={axisOptions(control)}
          onChange={(value) => onAxisChange(control.field, value)}
        />
      </div>
    );
  };

  return (
    <div className={styles.body}>
      {banner}

      {/* Axes donnée : rendus depuis le descripteur de la famille. Une famille
          peut avoir un contrôle que seule une application sait rendre (le style
          planning du Proposal) — d'où renderControl. */}
      {controls.map((control) => {
        const custom = renderControl?.(control);
        if (custom !== undefined && custom !== null) return custom;
        return defaultControl(control);
      })}

      {columnsConfigurable ? (
        <div>
          <div className={styles.sectionHeader}>
            <span className={styles.fieldLabel} style={{ marginBottom: 0 }}>{t('bpu-columns')}</span>
            {onResetAllNames && (
              <widgets.TextButton
                name="reset"
                title={t('bpu-reset-all-column-names')}
                disabled={!hasAnyCustomLabel}
                onClick={onResetAllNames}
              >
                {t('bpu-reset-all-labels')}
              </widgets.TextButton>
            )}
          </div>
          {loading ? (
            <div className={styles.loading}><widgets.Spinner /></div>
          ) : (
            <div className={styles.columns}>
              {columns.map((column, index) => (
                <ColumnRow
                  key={column.label}
                  column={column}
                  index={index}
                  total={columns.length}
                  isLastVisible={column.label === lastVisibleLabel}
                  widgets={widgets}
                  useColumnDrag={useColumnDrag}
                  t={t}
                  onToggleHidden={onToggleHidden}
                  onRename={onRename}
                  onResetName={onResetName}
                  onWidth={onWidth}
                  onAlign={onAlign}
                  onMove={onMove}
                />
              ))}
            </div>
          )}
          {totalsColumnHidden && (
            <div className={styles.warning} role="status">{t('table-totals-column-hidden')}</div>
          )}
        </div>
      ) : columnsUnavailableNote && (
        <div className={styles.note}>{columnsUnavailableNote}</div>
      )}

      {/* Aperçu fourni par l'hôte : celui d'un tableau custom montre ses vraies
          lignes (cf. CustomTablePreview), rien à déduire d'une forme. */}
      {preview && (
        <div>
          <div className={styles.fieldLabel}>{t('preview')}</div>
          {preview}
        </div>
      )}

      {/* Aperçu structurel : il doit répondre même quand la table n'a renvoyé
          aucune ligne (éditeur de template, document vide). */}
      {!preview && <div>
        <div className={styles.fieldLabel}>{t('preview')}</div>
        {shapedRows && onMergeChange && (
          <div className={`${styles.note} ${styles.previewHint}`}>{t('table-preview-merge-hint')}</div>
        )}
        {/* Sans forme déclarée, le tableau n'a que des lignes de données :
            l'aperçu n'est qu'un exemple de colonnes, rien à y régler. */}
        {!shapedRows && onMergeChange && (
          <div className={`${styles.note} ${styles.previewHint}`} role="status">
            {t('table-preview-no-row-structure')}
          </div>
        )}
        {/* Avertissement de l'hôte, juste au-dessus du tableau (ex. box à rafraîchir). */}
        {previewNotice && (
          <div className={`${styles.warning} ${styles.previewNotice}`} role="status">{previewNotice}</div>
        )}
        <div className={styles.previewScroll}>
          <table className={styles.previewTable} data-styled={!!previewStyle || undefined}>
            <thead>
              <tr>
                {visible.map((col, colIdx) => (
                  <th key={col.label} style={{ ...headerStyle(col.label, colIdx), width: `${col.width}%` }}>
                    {cellContent(col.customLabel ?? col.label)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shapedRows && shapedRows.map((row, rowIdx) => (
                <tr
                  key={row.key}
                  data-kind={row.kind}
                  data-clickable={canSelectRow(row) || undefined}
                >
                  {visible.map((col, colIdx) => {
                    // Fusion : la cellule d'ancrage couvre `span` colonnes, celles
                    // qu'elle recouvre disparaissent.
                    const { merge } = row;
                    if (merge && colIdx > merge.from && colIdx < merge.from + merge.span) return null;
                    const isAnchor = merge && colIdx === merge.from;
                    const source = (isAnchor && merge.contentColumn) || col.label;
                    return (
                      <td
                        key={col.label}
                        colSpan={isAnchor ? merge.span : undefined}
                        style={cellStyle(rowIdx, source, isAnchor ? colIdx + merge.span - 1 : colIdx, colIdx)}
                        data-first={source === previewLayout.labelColumn || undefined}
                        data-selected={isCellSelected(row, colIdx) || undefined}
                        onClick={(event) => selectCell(row, colIdx, event)}
                      >
                        {cellContent(row.cells[source])}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {!shapedRows && Array.from({ length: previewRows }, (_, rowIdx) => {
                const sample = previewSample?.(rowIdx + 1) ?? {};
                return (
                  <tr key={rowIdx}>
                    {visible.map((col, colIdx) => (
                      <td
                        key={col.label}
                        style={cellStyle(rowIdx, col.label, colIdx, colIdx)}
                        data-first={col.label === sampleLabelColumn || undefined}
                      >
                        {col.type === 'image'
                          ? <img className={styles.previewImage} src={placeholderImage} alt="" aria-hidden="true" />
                          : cellContent(sample[col.label]
                            ?? (col.label === sampleLabelColumn ? `${t('bpu-sample-row')} ${rowIdx + 1}` : '—'))}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {shapedRows && onMergeChange && (
          <div className={styles.previewActions}>
            {/* Retire toutes les règles : chaque ligne revient à sa fusion par défaut. */}
            <span className={styles.previewReset}>
              <widgets.TextButton
                name="reset"
                disabled={mergeOverrides.length === 0}
                onClick={() => setMergeOption('overrides', [])}
              >
                {t('table-merge-reset')}
              </widgets.TextButton>
            </span>
            <widgets.TextButton
              name="merge"
              disabled={!selectionMergeable}
              title={selectedRow && !selectionMergeable && selection.c1 > selection.c0
                ? t('table-merge-range-filled-hint')
                : ''}
              onClick={mergeSelection}
            >
              {t('table-merge-cells')}
            </widgets.TextButton>
            <widgets.TextButton
              name="unmerge"
              disabled={!selectedRow?.labelMerge?.merged}
              onClick={unmergeSelection}
            >
              {t('table-unmerge-cells')}
            </widgets.TextButton>
          </div>
        )}
      </div>}

      {/* Fusion auto des lignes-titre et des lignes vides (tableConfig.merge),
          et liste des règles. Sans les interrupteurs (Proposal), la section
          disparaît : la fusion se lit et se règle dans l'aperçu. */}
      {onMergeChange && showMergeSwitches && (
        <div>
          <div className={styles.fieldLabel}>{t('table-merge-section')}</div>
          {showMergeSwitches && (
            <>
              <div className={`${styles.switchRow} ${styles.mergeSwitch}`}>
                <span>{t('table-merge-label-rows')}</span>
                <widgets.Switch
                  ariaLabel={t('table-merge-label-rows')}
                  checked={mergeOptions?.labelRows ?? true}
                  onChange={(checked) => setMergeOption('labelRows', checked)}
                />
              </div>
              <div className={`${styles.switchRow} ${styles.mergeSwitch}`}>
                <span>{t('table-merge-empty-rows')}</span>
                <widgets.Switch
                  ariaLabel={t('table-merge-empty-rows')}
                  checked={mergeOptions?.emptyRows ?? true}
                  onChange={(checked) => setMergeOption('emptyRows', checked)}
                />
              </div>
            </>
          )}
          {mergeOverrides.length > 0 && (
            <>
              <div className={styles.mergeOverrides}>
                <span className={styles.note}>{t('table-merge-overrides')}</span>
                <widgets.TextButton name="reset" onClick={() => setMergeOption('overrides', [])}>
                  {t('table-merge-overrides-reset')}
                </widgets.TextButton>
              </div>
              {mergeOverrides.map((o) => (
                <div key={mergeRuleKey(o)} className={styles.mergeRule}>
                  <span className={styles.mergeRuleLabel}>{ruleName(o)}</span>
                  <span className={styles.mergeRuleRange}>{ruleRange(o)}</span>
                  <widgets.IconButton
                    name="reset"
                    title={t('table-merge-rule-remove')}
                    ariaLabel={`${t('table-merge-rule-remove')} ${ruleName(o)}`}
                    onClick={() => removeRule(o)}
                  />
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
